/**
 * New Year phone wallpapers — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const newYearPhoneWallpapersPack: ToilePack = {
    slug: 'new-year-phone-wallpapers',
    category: 'season',
    name: 'New Year phone wallpapers',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'fireworks', label: 'Fireworks', subject: 'Fireworks bursting low over a dark city skyline at the bottom of the frame, a calm night sky above'},
        {key: 'clock', label: 'Clock near midnight', subject: 'A gold clock face with hands near twelve at the bottom of the frame, a calm dark ground above'},
        {key: 'glass', label: 'Champagne glass', subject: 'A champagne flute with bubbles and confetti falling at the bottom of the frame, a calm ground above'},
    ],
    styles: [
        {key: 'burst', label: 'Fireworks', style: 'atmospheric digital painting of fireworks over a skyline, deep night', palette: 'Deep navy, gold and pink bursts', framing: 'The focal point low, the sky calm above for widgets'},
        {key: 'deco', label: 'Deco', style: 'art deco illustration, geometric, gold on black', palette: 'Black and gold', framing: 'A quiet center for icons, the motif at the edges'},
        {key: 'minimal', label: 'Minimal', style: 'minimal flat illustration, few shapes, lots of calm ground', palette: 'Gold on cream', framing: 'The focal point in the lower third and the upper half calm for the clock and icons'},
    ],
    directions: ['a party hat instead', 'in silver and blue', 'for a tablet screen'],
    directionHint: 'try: a party hat instead',
    seo: {
        title: 'New Year phone wallpapers: fireworks, clock',
        metaDescription: 'Nine New Year lock screen wallpapers — fireworks over a skyline, a midnight clock, a champagne glass — in fireworks, deco or minimal style.',
        h1: 'New Year phone wallpaper: fireworks, midnight clock, champagne glass',
        tagline: 'Nine wallpapers, three midnight scenes, three styles each — the top stays calm so the clock and icons sit clean.',
        intro: "A lock screen has to leave room for the clock — the top can't be busy. These nine set fireworks bursting low over a skyline, a gold clock face near twelve, and a champagne flute with falling confetti, each as fireworks, art deco or minimal. Ask for a different scene, like a party hat, and the same calm top carries it.",
        useCases: [
            'an iPhone or Android lock screen',
            "a New Year's Eve phone background",
            'a calm background behind widgets and the clock',
            'a matching wallpaper across phone and tablet',
        ],
        faq: [
            {q: 'Will the clock and icons stay readable over the wallpaper?', a: 'Yes — every wallpaper keeps its top calm and low-contrast, so the clock, notifications and app icons stay legible over it.'},
            {q: 'What size do these render at?', a: 'Portrait phone proportions, composed so the focal point sits low and the top third stays quiet.'},
            {q: 'Can I request a different scene, like a party hat?', a: 'Yes — the direction field takes any midnight scene, and the same three styles render it with a calm top.'},
            {q: "What's the difference between fireworks, deco and minimal?", a: 'Fireworks is an atmospheric night painting of bursts over a skyline, deco is a geometric gold-on-black illustration, and minimal is a flat few-shape gold-on-cream illustration.'},
        ],
    },
}
